import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { enumSimilarHtsSuggestionSource } from '@data-layer/enums/common';
import { HtsChatResultModel, SimilarHtsSuggestionModel } from '@data-layer/models/common';
import { FavoriteHtsNumberModel } from '@data-layer/models/common/favorite-hts-number.model';
import { UtilsHelper } from '@helpers/utils-helper';
import { HtsAssistantHelper, IHtsCodeGroup } from '@shared/hts-assistant/helpers/hts-assistant.helper';

/** The card's own view of whichever model it was given. */
interface IHtsCodeCardData {
  htsNumber: string | null;
  title: string | null;
  description: string;
  matchedText: string | null;
  /** Suggestion only: i18n key of where it comes from (catalog, an earlier conversation, a released entry). */
  sourceKey: string | null;
  isWithdrawn: boolean;
  isFromAssistant: boolean;
}

const EMPTY_DATA: IHtsCodeCardData = {
  htsNumber: null,
  title: null,
  description: '',
  matchedText: null,
  sourceKey: null,
  isWithdrawn: false,
  isFromAssistant: false,
};

const SOURCE_KEYS: { [source: number]: string } = {
  [enumSimilarHtsSuggestionSource.Catalog]: 'BINA_CATALOG.SUGGESTION_SOURCE_CATALOG',
  [enumSimilarHtsSuggestionSource.Chat]: 'BINA_CATALOG.SUGGESTION_SOURCE_CHAT',
  [enumSimilarHtsSuggestionSource.ReleasedEntry]: 'BINA_CATALOG.SUGGESTION_SOURCE_RELEASED',
  [enumSimilarHtsSuggestionSource.CustomsBroker]: 'BINA_CATALOG.SUGGESTION_SOURCE_BROKER',
};

/**
 * The one card for an HTS code (#13533): a saved catalog item, a similar product the company classified before, or the
 * assistant's result — digit boxes, title, the (tariff) description cut to a few lines behind "Show more", meta lines, and
 * an action slot holding the Insert button, the "Current" mark or whatever the host projects (`[card-actions]`); notes go to
 * `[card-footer]`. Used by the fast-search dropdown, the Bina catalog sidebar and calculator, the assistant's suggestions
 * step and its chat result.
 */
@Component({
  selector: 'app-hts-code-card',
  templateUrl: './hts-code-card.component.html',
  styleUrls: ['./hts-code-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HtsCodeCardComponent implements OnChanges, AfterViewChecked {
  /** Exactly one of the three models is given. */
  @Input() public favoriteHtsNumber: FavoriteHtsNumberModel | null = null;
  @Input() public suggestion: SimilarHtsSuggestionModel | null = null;
  @Input() public result: HtsChatResultModel | null = null;
  /** Dropdown row: no border / radius, the rows are separated by lines. */
  @Input() public isCompact: boolean = false;
  @Input() public isShowHoverBackground: boolean = false;
  @Input() public isShowMoreEnabled: boolean = true;
  /** Always the cut text, never measured (the calculator sidebar). */
  @Input() public isAlwaysTruncated: boolean = false;
  @Input() public maxDescriptionLines: number = 3;
  @Input() public descriptionCharLimit: number = 205;
  /** The Insert action in the action slot (never on a withdrawn code). */
  @Input() public isShowInsert: boolean = false;
  @Input() public isInserting: boolean = false;
  /** The code the line already holds (assistant step on a verified line): a "Current" mark instead of Insert. */
  @Input() public isCurrent: boolean = false;
  /** #13411: companies with the classification assistant see which items came from it and which codes were withdrawn. */
  @Input() public isShowAssistantTag: boolean = false;
  /** Lists that mix catalog items with suggestions: a grey "Bina Catalog" line under the description. */
  @Input() public isShowCatalogSource: boolean = false;
  /** Lists with suggestions from several sources (#13534): a grey line saying where the suggestion comes from. */
  @Input() public isShowSuggestionSource: boolean = false;
  /** Result superseded by a restart: a lighter border. */
  @Input() public isStale: boolean = false;
  /** History, admin: result of a superseded conversation — gray. */
  @Input() public isDimmed: boolean = false;
  @Output() public insert: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('description', { static: false })
  public descriptionRef: ElementRef<HTMLElement>;

  public data: IHtsCodeCardData = EMPTY_DATA;
  public codeGroups: IHtsCodeGroup[] = [];
  public isExpanded: boolean = false;
  /** The full description takes more than `maxDescriptionLines`: collapsed it is cut to `descriptionCharLimit` behind "Show more". */
  public isTruncated: boolean = false;

  /** A new description is rendered in full first, then measured once. */
  private isMeasurePending: boolean = false;

  constructor(private cdr: ChangeDetectorRef) {}

  public ngOnChanges(changes: SimpleChanges): void {
    const isModelChanged: boolean = !!(changes.favoriteHtsNumber || changes.suggestion || changes.result || changes.isShowAssistantTag);
    const isCutChanged: boolean = !!(changes.isAlwaysTruncated || changes.maxDescriptionLines);
    if (!isModelChanged && !isCutChanged) {
      return;
    }
    const data: IHtsCodeCardData = isModelChanged ? this.toData() : this.data;
    if (data.description !== this.data.description || isCutChanged) {
      this.isExpanded = false;
      this.isTruncated = false;
      this.isMeasurePending = !this.isAlwaysTruncated;
    }
    this.data = data;
    this.codeGroups = HtsAssistantHelper.toCodeGroups(data.htsNumber);
  }

  public ngAfterViewChecked(): void {
    if (!this.isMeasurePending || !this.descriptionRef || !this.descriptionRef.nativeElement) {
      return;
    }
    this.isMeasurePending = false;
    const element: HTMLElement = this.descriptionRef.nativeElement;
    const lineHeight: number = parseInt(getComputedStyle(element).lineHeight, 10);
    if (!lineHeight) {
      return;
    }
    const isTruncated: boolean = Math.floor(element.scrollHeight / lineHeight) > this.maxDescriptionLines;
    if (isTruncated !== this.isTruncated) {
      this.isTruncated = isTruncated;
      this.cdr.detectChanges();
    }
  }

  public get isShowTitle(): boolean {
    return !!this.data.title;
  }

  public get isShowDescription(): boolean {
    return !!this.data.description;
  }

  public get isShowBody(): boolean {
    return this.isShowDescription || this.isShowMatched || this.isShowCatalogSource || this.isShowSource;
  }

  public get isShowSource(): boolean {
    return this.isShowSuggestionSource && !!this.data.sourceKey;
  }

  public get displayedDescription(): string {
    const isCut: boolean = this.isAlwaysTruncated || (this.isTruncated && !this.isExpanded);
    return isCut ? UtilsHelper.limitLength(this.data.description, this.descriptionCharLimit) : this.data.description;
  }

  public get isShowMore(): boolean {
    return this.isShowMoreEnabled && !this.isAlwaysTruncated && this.isTruncated;
  }

  public get isShowInsertButton(): boolean {
    return this.isShowInsert && !this.isCurrent && !this.data.isWithdrawn;
  }

  /** The product the suggestion was matched with, unless the title already is that text. */
  public get isShowMatched(): boolean {
    return !!this.data.matchedText && this.data.matchedText !== this.data.title;
  }

  public get isAssistantTagVisible(): boolean {
    return this.isShowAssistantTag && this.data.isFromAssistant;
  }

  public toggleShowMore(): void {
    this.isExpanded = !this.isExpanded;
  }

  public onInsert(): void {
    if (!this.isInserting) {
      this.insert.emit();
    }
  }

  public trackByIndex(index: number): number {
    return index;
  }

  private toData(): IHtsCodeCardData {
    if (this.favoriteHtsNumber) {
      const item: FavoriteHtsNumberModel = this.favoriteHtsNumber;
      return {
        htsNumber: item.htsNumber || null,
        title: item.title || null,
        description: item.description || '',
        matchedText: null,
        sourceKey: null,
        // a code withdrawn in an HTS update: still listed (the user sees why it is gone), never insertable
        isWithdrawn: this.isShowAssistantTag && item.isActive === false,
        isFromAssistant: !!item.isFromAssistant,
      };
    }
    if (this.suggestion) {
      const suggestion: SimilarHtsSuggestionModel = this.suggestion;
      return {
        htsNumber: suggestion.htsNumber || null,
        // catalog title, else the source line's description (the backend falls back to the matched text itself)
        title: suggestion.title || suggestion.matchedText || null,
        description: HtsAssistantHelper.joinTariffLevels(suggestion.tariffDescription),
        matchedText: suggestion.matchedText || null,
        sourceKey: suggestion.source !== null && suggestion.source !== undefined ? SOURCE_KEYS[suggestion.source] || null : null,
        isWithdrawn: false,
        isFromAssistant: false,
      };
    }
    if (this.result) {
      return {
        ...EMPTY_DATA,
        htsNumber: this.result.htsNumber || null,
        description: HtsAssistantHelper.joinTariffLevels(this.result.tariffDescription),
      };
    }
    return EMPTY_DATA;
  }
}
